import updateAccountPasswordApi from '@/api/patch/account-profile/update-account-password';
import Alert from '@/components/alert/alert-component';
import Card from '@/components/card/card-component';
import SubmitCard from '@/components/card/submit-card-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { Col, Row } from '@/components/grid/grid-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import { useMutation } from '@/hooks/use-api';
import { Heading3Text } from '@/utils/global-fonts';
import { passwordRules, requiredValidationMessage } from '@/utils/validations';
import { MdLockOutline } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import Styled from './password-edit-style';

/**
 * Edit a profile
 * @returns {JSX.Element}
 */
const PasswordEdit = () => {
  const navigate = useNavigate();

  const [updateMyAccountPassword, { loading: updateMyAccountPasswordLoading }] =
    useMutation(updateAccountPasswordApi);

  const onSubmit = (values) => {
    updateMyAccountPassword(values).then(() => {
      ConfirmationModal({
        title: 'Change Password',
        confirmButtonText: 'OK',
        showCancelButton: false,
        text: 'Your password has been updated successfully.',
        onConfirm: () => {
          navigate('/app/profile-management/profile');
          Alert({
            icon: 'success',
            text: 'Password updated successfully',
          });
        },
      });
    });
  };

  const loading = updateMyAccountPasswordLoading;

  return (
    <Styled.Main>
      <FormWrapper defaultValues={{}} onSubmit={onSubmit}>
        <Card>
          <Row direction="row">
            <Col xs={12}>
              <Heading3Text>Change Password</Heading3Text>
            </Col>
          </Row>
          <Styled.StyledBlockRow>
            <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
              <Input
                iconLeft={<MdLockOutline />}
                label="Current Password"
                placeholder="Current password"
                name="current_password"
                disabled={loading}
                registerOptions={{
                  validate: (value) => {
                    let errorMessage = null;
                    passwordRules.forEach((rule) => {
                      if (!rule.pattern.test(value)) {
                        errorMessage = rule.message;
                      }
                    });
                    return errorMessage || true;
                  },
                }}
                type="password"
              />
            </Col>
            <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
              <Input
                iconLeft={<MdLockOutline />}
                label="Create New Password"
                placeholder="New password"
                name="new_password"
                disabled={loading}
                registerOptions={{
                  validate: (value) => {
                    let errorMessage = null;
                    passwordRules.forEach((rule) => {
                      if (!rule.pattern.test(value)) {
                        errorMessage = rule.message;
                      }
                    });
                    return errorMessage || true;
                  },
                }}
                type="password"
              />
            </Col>
            <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
              <Input
                iconLeft={<MdLockOutline />}
                label="Confirm New Password"
                placeholder="Confirm password"
                type="password"
                icon="password"
                name="confirm_password"
                disabled={loading}
                registerOptions={{
                  required: requiredValidationMessage,
                  /**
                   * Validate password match
                   * @param {string} value - The value of the input
                   * @param {Function} getValues - The getValues function from React Hook Form
                   * @returns {string} The error message
                   */
                  validate: (value, getValues) =>
                    value === getValues('new_password') ||
                    'Passwords do not match',
                }}
              />
            </Col>
          </Styled.StyledBlockRow>
        </Card>
        <SubmitCard
          padding="0"
          primaryButtonTitle="Update"
          primaryButtonType="submit"
          isLoading={loading}
          primaryButtonDisabled={false}
          primaryButtonWidth="112px"
          secondaryButtonType="button"
          secondaryButtonDisabled={false}
          secondaryButtonWidth="112px"
          secondaryButtonTitle="Cancel"
          submitClickSecondary={() => {
            navigate(-1);
          }}
        />
      </FormWrapper>
    </Styled.Main>
  );
};

PasswordEdit.propTypes = {};

export default PasswordEdit;
